import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import config from "../../config";
import { features } from "../../config/features";
import {
  validateVerificationHash,
  validateSelfieAnonymous,
  analyzeDocumentAnonymous,
  requestManualReviewAnonymous,
} from "../../actions/email-verification.action";

// Reuse webcam + upload patterns from verification-page.js
const TABS = [
  { id: "selfie", label: "Selfie Verification", icon: "fa-portrait" },
  { id: "document", label: "Document Verification", icon: "fa-file-certificate" },
];

// ─── Mobile detection ─────────────────────────────────────────────────────────
const isMobileDevice = () => {
  return /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );
};

// ─── Upload Zone ──────────────────────────────────────────────────────────────
const UploadZone = ({ accept, label, onFile, file, allowCamera = false, facingMode = "user", showUpload = true }) => {
  const inputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  useEffect(() => {
    if (!file) { setPreview(null); return; }
    if (!file.type.startsWith("image/")) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraActive(false);
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const startCamera = async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      setCameraActive(true);
      setTimeout(() => {
        if (videoRef.current && streamRef.current) {
          videoRef.current.srcObject = streamRef.current;
          videoRef.current.play().catch(() => {});
        }
      }, 100);
    } catch (err) {
      setCameraError(
        err.name === "NotAllowedError"
          ? "Camera access was denied. Please allow camera access in your browser settings."
          : "Could not access the camera."
      );
    }
  };

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    stopCamera();
    const byteString = atob(dataUrl.split(",")[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
    const blob = new Blob([ab], { type: "image/jpeg" });
    const capturedFile = new File([blob], `capture-${Date.now()}.jpg`, { type: "image/jpeg" });
    onFile(capturedFile);
  };

  const handleFile = (f) => {
    if (f) onFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  // Camera active — show video feed
  if (cameraActive) {
    return (
      <div className="space-y-3">
        <div className="rounded-xl overflow-hidden bg-black relative">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full"
            style={{ transform: facingMode === "user" ? "scaleX(-1)" : "none" }}
          />
          {facingMode === "user" && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div
                className="rounded-full border-2 border-white border-opacity-60"
                style={{ width: "45%", height: "75%", boxShadow: "0 0 0 9999px rgba(0,0,0,0.25)" }}
              />
            </div>
          )}
        </div>
        <p className="text-xs text-gray-400 text-center">
          {facingMode === "user"
            ? "Centre your face in the oval. Ensure good lighting and look straight at the camera."
            : "Place your ID document in frame. Ensure good lighting and avoid glare."}
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleCapture}
            className="flex-1 py-2.5 rounded-lg text-white bg-[#9aa82d] hover:bg-[#7d8a1f] text-sm font-medium"
          >
            Capture Photo
          </button>
          <button
            type="button"
            onClick={stopCamera}
            className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm font-medium"
          >
            Cancel
          </button>
        </div>
        <canvas ref={canvasRef} className="hidden" />
      </div>
    );
  }

  // Preview — show captured/uploaded image
  if (preview) {
    return (
      <div className="space-y-3">
        <div className="rounded-xl overflow-hidden border border-gray-200">
          <img src={preview} alt="Preview" className="w-full max-h-64 object-contain bg-gray-50" />
        </div>
        <div className="flex gap-3">
          {allowCamera && (
            <button
              type="button"
              onClick={() => { onFile(null); startCamera(); }}
              className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm font-medium flex items-center justify-center gap-2"
            >
              <i className="fal fa-redo"></i> Retake
            </button>
          )}
          {showUpload && (
            <button
              type="button"
              onClick={() => { onFile(null); inputRef.current?.click(); }}
              className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm font-medium flex items-center justify-center gap-2"
            >
              <i className="fal fa-folder-open"></i> Choose File
            </button>
          )}
        </div>
        {showUpload && (
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => handleFile(e.target.files[0])}
          />
        )}
      </div>
    );
  }

  // Default — upload zone or camera-only
  return (
    <div className="space-y-3">
      {showUpload && (
        <div
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
            dragOver ? "border-[#9aa82d] bg-[#f5f7eb]" : "border-gray-300 hover:border-gray-400"
          }`}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <i className="fal fa-cloud-upload text-3xl text-gray-400 mb-3"></i>
          <p className="text-sm text-gray-600">{label}</p>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => handleFile(e.target.files[0])}
          />
        </div>
      )}
      <div className="flex gap-3">
        {allowCamera && (
          <button
            type="button"
            onClick={startCamera}
            className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm font-medium flex items-center justify-center gap-2"
          >
            <i className="fal fa-camera"></i> Use Camera
          </button>
        )}
        {showUpload && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm font-medium flex items-center justify-center gap-2"
          >
            <i className="fal fa-folder-open"></i> Browse Files
          </button>
        )}
      </div>
      {cameraError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">{cameraError}</div>
      )}
    </div>
  );
};

// ─── Selfie Tab ───────────────────────────────────────────────────────────────
const SelfieTab = ({ hash }) => {
  const dispatch = useDispatch();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const res = await dispatch(validateSelfieAnonymous(hash, file));
      setResult(res.Result);
    } catch (err) {
      setError(err?.Result || "Selfie validation failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">Upload a clear selfie photo for identity verification.</p>
      {!result && (
        <>
          <UploadZone accept="image/jpeg,image/png,image/tiff,image/bmp,image/heif" label="Take a selfie photo" onFile={setFile} file={file} allowCamera facingMode="user" showUpload={config.VERIFICATION_TEST_MODE} />
          {file && <p className="text-sm text-gray-500">Selected: {file.name}</p>}
          <button
            onClick={handleSubmit}
            disabled={!file || loading}
            className="w-full py-2.5 rounded-lg text-white bg-[#9aa82d] hover:bg-[#7d8a1f] disabled:opacity-50 transition-colors"
          >
            {loading ? "Validating..." : "Verify and Send"}
          </button>
        </>
      )}
      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-600">{error}</div>}
      {result && (
        <div className={`rounded-lg p-4 text-sm ${result.IsValid ? "bg-green-50 border border-green-200 text-green-700" : "bg-red-50 border border-red-200 text-red-600"}`}>
          {result.IsValid ? "Selfie verified successfully!" : "Selfie did not meet quality requirements."}
          {result.RejectionReasons?.length > 0 && (
            <ul className="mt-2 list-disc list-inside">
              {result.RejectionReasons.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          )}
          {!result.IsValid && (
            <button
              type="button"
              onClick={() => { setResult(null); setFile(null); setError(null); }}
              className="mt-3 w-full py-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-100 text-sm font-medium transition-colors"
            >
              Retake Selfie
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Document Tab ─────────────────────────────────────────────────────────────
const DocumentTab = ({ hash }) => {
  const dispatch = useDispatch();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const res = await dispatch(analyzeDocumentAnonymous(hash, file));
      setResult(res.Result);
    } catch (err) {
      setError(err?.Result || "Document verification failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">Upload a photo of your government-issued ID document.</p>
      {!result && (
        <>
          <UploadZone accept="image/jpeg,image/png,image/tiff,image/bmp,image/heif,application/pdf" label="Take a photo of your ID document" onFile={setFile} file={file} allowCamera facingMode="environment" showUpload={config.VERIFICATION_TEST_MODE} />
          {file && <p className="text-sm text-gray-500">Selected: {file.name}</p>}
          <button
            onClick={handleSubmit}
            disabled={!file || loading}
            className="w-full py-2.5 rounded-lg text-white bg-[#9aa82d] hover:bg-[#7d8a1f] disabled:opacity-50 transition-colors"
          >
            {loading ? "Analyzing..." : "Verify and Send"}
          </button>
        </>
      )}
      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-600">{error}</div>}
      {result && (
        <div className={`rounded-lg p-4 text-sm ${
          result.Status === "Valid"
            ? "bg-green-50 border border-green-200 text-green-700"
            : result.Status === "Invalid"
              ? "bg-red-50 border border-red-200 text-red-600"
              : "bg-yellow-50 border border-yellow-200 text-yellow-700"
        }`}>
          {result.Status === "Valid"
            ? "Document verified successfully!"
            : result.Status === "Invalid"
              ? "Document verification failed."
              : "Document requires manual review."}
          {result.Reasons?.length > 0 && (
            <ul className="mt-2 list-disc list-inside">
              {result.Reasons.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          )}
          {result.Status === "Invalid" && (
            <button
              type="button"
              onClick={() => { setResult(null); setFile(null); setError(null); }}
              className="mt-3 w-full py-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-100 text-sm font-medium transition-colors"
            >
              Retake Photo
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
export const VerifyEmailPage = () => {
  const { id, hash } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [errorMsg, setErrorMsg] = useState(null);
  const [activeTab, setActiveTab] = useState("selfie");
  const [manualReviewRequested, setManualReviewRequested] = useState(false);

  const [manualReviewLoading, setManualReviewLoading] = useState(false);
  const handleRequestManualReview = async () => {
    setManualReviewLoading(true);
    try {
      await dispatch(requestManualReviewAnonymous(hash));
      setManualReviewRequested(true);
    } catch {
      setManualReviewLoading(false);
    }
  };

  useEffect(() => {
    // Feature disabled — do not render the identity-verification flow
    if (!features.identityVerification) {
      navigate("/", { replace: true });
      return;
    }

    // Redirect mobile devices
    if (isMobileDevice()) {
      navigate(`/verify-mobile/${id}/${hash}`, { replace: true });
      return;
    }

    // Validate hash
    dispatch(validateVerificationHash(hash))
      .then((res) => {
        setValid(true);
        setFirstName(res.Result?.FirstName || "");
      })
      .catch(() => {
        setErrorMsg("This verification link is invalid or has expired.");
      })
      .finally(() => setValidating(false));
  }, [id, hash, dispatch, navigate]);

  if (validating) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Validating verification link...</div>
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <i className="fal fa-exclamation-triangle text-red-500 text-2xl"></i>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Invalid Link</h1>
          <p className="text-sm text-gray-500">{errorMsg}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center p-4 sm:p-8">
      <div className="w-full max-w-2xl space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-[#98a62d] bg-opacity-10 flex items-center justify-center">
              <i className="fal fa-shield-check text-[#98a62d] text-lg"></i>
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Identity Verification</h1>
              <p className="text-sm text-gray-500">
                {firstName ? `Hi ${firstName}, please` : "Please"} complete the verification steps below.
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="flex">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 text-sm font-medium transition-colors relative ${
                    activeTab === tab.id
                      ? "text-[#9aa82d]"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <i className={`fal ${tab.icon}`}></i>
                  {tab.label}
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#9aa82d]"></div>
                  )}
                </button>
              ))}
            </nav>
          </div>
          <div className="p-6">
            {activeTab === "selfie" && <SelfieTab hash={hash} />}
            {activeTab === "document" && <DocumentTab hash={hash} />}
          </div>
        </div>

        {/* No camera option */}
        {!manualReviewRequested ? (
          <div className="text-center py-2">
            <button
              onClick={handleRequestManualReview}
              disabled={manualReviewLoading}
              className="text-sm text-gray-400 hover:text-gray-600 underline transition-colors disabled:opacity-50"
            >
              {manualReviewLoading ? "Requesting..." : "I don't have access to a camera"}
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-3">
              <i className="fal fa-clock text-yellow-500 text-xl"></i>
            </div>
            <p className="text-sm font-medium text-gray-800">Manual review requested</p>
            <p className="text-sm text-gray-500 mt-1">An administrator will review your account and verify your identity.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;
